// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import FeedTabs from '@app/features/feed/state/FeedTabs';
import FeedTopics from '@app/features/feed/state/FeedTopics';
import {FEED_COMPOSER_TEXT_ID} from '@app/features/feed/utils/FeedCategoryUtils';
import {isFeedTopicsEnabled} from '@app/features/feed/utils/FeedUtils';
import {mediaUrl} from '@app/features/messaging/utils/MessagingUrlUtils';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import type {FeedTopicResponse} from '@fluxer/schema/src/domains/feed/FeedTopicSchemas';

// The trending row shows the newest of the live topics; the server sends at most 20.
export const FEED_TOPIC_STRIP_MAX = 8;

const NO_TOPICS: ReadonlyArray<FeedTopicResponse> = [];

// Read on every render, so the limit turned off hides every topic surface at once.
export function shownFeedTopics(): ReadonlyArray<FeedTopicResponse> {
	return isFeedTopicsEnabled() ? FeedTopics.topics : NO_TOPICS;
}

export function selectedFeedTopicId(): string | null {
	return isFeedTopicsEnabled() ? FeedTabs.selectedTopicId : null;
}

// A topic opens in the feed page's main column: from a post page, a section page or a modal the feed comes back.
export function openFeedTopic(topicId: string): void {
	FeedTabs.openTopic(topicId);
	if (RouterUtils.getCurrentPath() !== Routes.FEED) RouterUtils.transitionTo(Routes.FEED);
}

// "Share in this topic": its page opens (the post box picks the topic) and the cursor goes to the post box once the
// page has drawn it.
export function shareInFeedTopic(topicId: string): void {
	if (FeedTabs.selectedTopicId !== topicId) openFeedTopic(topicId);
	requestAnimationFrame(() => document.getElementById(FEED_COMPOSER_TEXT_ID)?.focus());
}

export function feedTopicImageUrl(topic: FeedTopicResponse, size: number): string | null {
	return topic.image ? mediaUrl(`branding/feed-topic-${topic.id}/${topic.image}.webp?size=${size}`) : null;
}

// Whole hours while one is left, then minutes; null once the topic takes no posts.
export function feedTopicTimeLeft(
	topic: Pick<FeedTopicResponse, 'ends_at' | 'status'>,
	now: number,
): {hours: number} | {minutes: number} | null {
	const left = Date.parse(topic.ends_at) - now;
	if (topic.status !== 'live' || !(left > 0)) return null;
	const minutes = Math.ceil(left / 60_000);
	return minutes < 60 ? {minutes} : {hours: Math.floor(minutes / 60)};
}
